import { useSystemColorSwitch } from '@unistocks/shared/hooks/use-system-colors';
import { Logger } from '@unistocks/shared/services/logger.service';
import {
  createContext,
  PropsWithChildren,
  use,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState
} from 'react';
import { isWeb } from 'tamagui';

type ModalContextType = {
  onOpenChange: (id: string, open: boolean) => void;
  deleteModal: (id: string) => void;
  getOpenModalsCount: () => number;
  topModalId: string | undefined;
};

/**
 * Context to manage modals in the app. It keeps track of the number of open modals and provides a callback to update the count.
 * It also provides a method to get the current count of open modals.
 */
const ModalContext = createContext<ModalContextType>(null as any);

export const ModalProvider = ({ children }: PropsWithChildren) => {
  const bodyEl = useRef<HTMLBodyElement>(null);
  const { toggleSystemColors } = useSystemColorSwitch();
  const openModalsCount = useRef(new Map<string, boolean>());
  const openModalStack = useRef<string[]>([]);
  const [topModalId, setTopModalId] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (isWeb) {
      bodyEl.current = document.querySelector('body');
    }
  }, []);

  const toggleBodyScrolling = (hasOpenModals: boolean) => {
    if (!bodyEl.current) return;

    if (hasOpenModals) {
      bodyEl.current.classList.add('overflow-hidden');
    } else {
      bodyEl.current.classList.remove('overflow-hidden');
    }
  };

  const getOpenModalsCount = useCallback(() => {
    let count = 0;
    openModalsCount.current.forEach(value => {
      if (value) {
        count += 1;
      }
    });
    return count;
  }, []);

  const onOpenChange = useCallback(
    (id: string, open: boolean) => {
      Logger.verbose(`ModalProvider: onOpenChange - id: ${id}, open: ${open}`);
      openModalsCount.current.set(id, open);

      if (open) {
        openModalStack.current = [...openModalStack.current.filter(i => i !== id), id];
      } else {
        openModalStack.current = openModalStack.current.filter(i => i !== id);
      }

      setTopModalId(openModalStack.current[openModalStack.current.length - 1]);

      const hasOpenModals = Array.from(openModalsCount.current.values()).some(isOpen => isOpen);

      // While there are open modals, force dark mode for system colors (android navigation bar, status bar)
      toggleSystemColors(hasOpenModals);
      toggleBodyScrolling(hasOpenModals);
    },
    [toggleSystemColors]
  );

  const deleteModal = useCallback((id: string) => {
    Logger.verbose(`ModalProvider: deleteModal - id: ${id}`);
    openModalsCount.current.delete(id);
    openModalStack.current = openModalStack.current.filter(i => i !== id);
    setTopModalId(openModalStack.current[openModalStack.current.length - 1]);
  }, []);

  return (
    <ModalContext.Provider value={{ onOpenChange, deleteModal, getOpenModalsCount, topModalId }}>
      {children}
    </ModalContext.Provider>
  );
};

/**
 * Register a component as a modal and automatically handle system color changes when it opens or closes.
 * @param isOpen - Whether the modal is open or not.
 * @returns The modal context values.
 */
export const useModalContext = (isOpen: boolean) => {
  const id = useId();
  const { onOpenChange, deleteModal, getOpenModalsCount, topModalId } = use(ModalContext);

  useEffect(() => {
    onOpenChange(id, isOpen);

    return () => {
      deleteModal(id);
    };
  }, [deleteModal, id, isOpen, onOpenChange]);

  const isFocused = topModalId === id;

  return { id, isFocused, getOpenModalsCount };
};
